import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { History, Warning } from '@mui/icons-material';
import { formatDateTimeJST } from '../../utils/dateUtils';
import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { getStatusStageLabel, type EvaluationStatus } from '../../utils/statusLabels';

interface RejectionHistoryItem {
  id: number;
  evaluation_id: string;
  rejected_by: string;
  rejected_by_name: string;
  rejected_from_status: string;
  rejected_to_status: string;
  rejection_reason: string;
  rejection_comment: string;
  rejected_at: string;
}

interface ReturnCommentHistoryProps {
  evaluationId: string;
  showAll?: boolean;
}

const getStatusText = (status: string): string =>
  getStatusStageLabel(status as EvaluationStatus);

const getRejectionLabel = (toStatus: string): { label: string; color: 'error' | 'warning' | 'info' } => {
  return {
    label: `${getStatusText(toStatus)}へ差し戻し`,
    color: 'warning' as const
  };
};

export const ReturnCommentHistory: React.FC<ReturnCommentHistoryProps> = ({
  evaluationId,
  showAll = false,
}) => {
  const [rejections, setRejections] = useState<RejectionHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRejectionHistory();
  }, [evaluationId]);

  const fetchRejectionHistory = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(
        `/evaluations/${evaluationId}/rejection-history`
      );

      if (response.data.success) {
        setRejections(response.data.data || []);
      }
    } catch (err) {
      // 404エラー（履歴なし）の場合は空配列を設定
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errAny = err as any;
      if (errAny.response?.status === 404 || errAny.response?.status === 403) {
        setRejections([]);
      } else {
        setError(extractErrorMessage(err, '差し戻し履歴の取得に失敗しました'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (rejections.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          差し戻し履歴はありません
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {rejections.map((rejection) => {
        const labelInfo = getRejectionLabel(rejection.rejected_to_status);

        return (
          <Card
            key={rejection.id}
            sx={{
              mb: 2,
              borderLeft: 4,
              borderColor: `${labelInfo.color}.main`,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Warning color={labelInfo.color} />
                  <Chip
                    label={labelInfo.label}
                    color={labelInfo.color}
                    size="small"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTimeJST(rejection.rejected_at) || '-'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  差し戻し者：{rejection.rejected_by_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getStatusText(rejection.rejected_from_status)} → {getStatusText(rejection.rejected_to_status)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  差し戻し理由：
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {rejection.rejection_comment}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        );
      })}

      {rejections.length > 0 && (
        <Box display="flex" alignItems="center" justifyContent="center" mt={2}>
          <History fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="caption" color="text.secondary">
            全{rejections.length}件の履歴を表示中
          </Typography>
        </Box>
      )}
    </Box>
  );
};
